/**
 * The vocabulary of a console capability, shared by the two places that
 * speak it: the Backoffice package editor and Feature access board (where an
 * operator decides who has a capability) and the console's dev toolbar
 * Locks view (where a developer sees the lock render). One definition, so
 * both say "A tab in Cadastre" and "a lock badge on the tab, the dialog
 * when it is clicked" in the same words, and a developer looking at one
 * recognises the other.
 *
 * The codes are common.FeatureSurfaceKinds (seeded by
 * common.Seed_FeatureSurfaceKinds); the names are that seed's names.
 */

/**
 * common.FeatureAvailabilityTypes: who may reach a feature at all. Available
 * for all is how a feature arrives; locked within package is the one value
 * that makes it depend on packages (a plan or add-on carrying one unlocks
 * it). Both are decided on the Backoffice Feature access board and nowhere else
 * (FUS-2787); the retired "requires custom access" (3) is folded into 2.
 */
export const FEATURE_AVAILABILITY_TYPE_CODE = {
  availableForAll: 1,
  lockedWithinPackage: 2,
} as const

// common.FeatureSurfaceKinds: what sort of console surface a capability is.
// A module (a sidebar item) and a panel (an object panel's root) both grant
// a whole module and head its card; a switch never reaches the catalogue
// (common.LockableFeatureSurfaces leaves it out).
export const FEATURE_SURFACE_KIND = {
  module: 1,
  tab: 2,
  subTab: 3,
  panelModule: 4,
  mapTool: 5,
  capability: 6,
  switch: 7,
  panel: 8,
} as const

export type FeatureSurfaceKindCode =
  (typeof FEATURE_SURFACE_KIND)[keyof typeof FEATURE_SURFACE_KIND]

/** The kind as the catalogue names it (common.Seed_FeatureSurfaceKinds). */
export const FEATURE_SURFACE_KIND_NAME: Record<FeatureSurfaceKindCode, string> =
  {
    [FEATURE_SURFACE_KIND.module]: "Module",
    [FEATURE_SURFACE_KIND.tab]: "Tab",
    [FEATURE_SURFACE_KIND.subTab]: "Sub-tab",
    [FEATURE_SURFACE_KIND.panelModule]: "Panel module",
    [FEATURE_SURFACE_KIND.mapTool]: "Map tool",
    [FEATURE_SURFACE_KIND.capability]: "Capability",
    [FEATURE_SURFACE_KIND.switch]: "Switch",
    [FEATURE_SURFACE_KIND.panel]: "Panel",
  }

/** A module root: a sidebar module or an object panel; grants the whole module. */
export function isModuleRootKind(kindCode: number) {
  return (
    kindCode === FEATURE_SURFACE_KIND.module ||
    kindCode === FEATURE_SURFACE_KIND.panel
  )
}

// Where in Fusion a capability of each kind lives, in an operator's words.
export const WHERE_BY_KIND: Partial<
  Record<FeatureSurfaceKindCode, (module: string) => string>
> = {
  [FEATURE_SURFACE_KIND.module]: (module) => `Everything in ${module}`,
  [FEATURE_SURFACE_KIND.panel]: (module) => `The whole ${module} panel`,
  [FEATURE_SURFACE_KIND.tab]: (module) => `A tab in ${module}`,
  [FEATURE_SURFACE_KIND.subTab]: (module) => `A sub-tab in ${module}`,
  [FEATURE_SURFACE_KIND.panelModule]: (module) => `A section in ${module}`,
  [FEATURE_SURFACE_KIND.mapTool]: () => "A tool on the map",
  [FEATURE_SURFACE_KIND.capability]: (module) => `Part of ${module}`,
}

// What a package-only decision does in Fusion for a customer without the
// package, per kind. The lock renders where the developer put the gate
// (ADR-0040): a badge on the item and the capability's dialog instead of the
// thing; a panel locks every tab; a section or an inline capability shows
// locked in place.
export const LOCK_PLACEMENT_BY_KIND: Partial<
  Record<FeatureSurfaceKindCode, (name: string, parent: string) => string>
> = {
  [FEATURE_SURFACE_KIND.module]: (name) =>
    `Locks the "${name}" item in the sidebar: a lock badge on it, and opening it shows the capability's dialog instead of the module.`,
  [FEATURE_SURFACE_KIND.panel]: (name) =>
    `Locks the whole "${name}" panel: every tab shows a lock badge and the locked notice instead of its content.`,
  [FEATURE_SURFACE_KIND.tab]: (name, parent) =>
    `Locks the "${name}" tab in ${parent}: a lock badge on the tab, the dialog when it is clicked.`,
  [FEATURE_SURFACE_KIND.subTab]: (name, parent) =>
    `Locks the "${name}" sub-tab under ${parent}: a lock badge on it, the dialog when it is clicked.`,
  [FEATURE_SURFACE_KIND.panelModule]: (name, parent) =>
    `Locks the "${name}" section in ${parent}: it shows locked in place.`,
  [FEATURE_SURFACE_KIND.mapTool]: (name) =>
    `Locks the "${name}" tool in the map toolbar: a lock badge on the button, the dialog when it is clicked.`,
  [FEATURE_SURFACE_KIND.capability]: (name, parent) =>
    `Locks "${name}" where it appears in ${parent}: a locked notice in its place, or nothing where the developer chose to hide it.`,
}

/**
 * A readable name read off a FeatureCode, for a surface whose text nobody
 * has authored yet: `UI_Cadastre_TabGovernmentCases` → "Government cases",
 * `UI_HousingUnit_Root` → "Housing unit". Both apps show it the same way,
 * so an unnamed capability is recognisable across them.
 */
export function humanizeFeatureCode(code: string) {
  const segments = code
    .replace(/^UI_/, "")
    .split("_")
    .filter((segment) => segment !== "" && segment !== "Root")
  const last = segments[segments.length - 1] ?? code
  const words = last
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(" ")
  return words
    .map((word, index) => {
      if (word === word.toUpperCase()) return word
      return index === 0 ? word : word.toLowerCase()
    })
    .join(" ")
}

/**
 * The Backoffice Feature access board, deep-linked to one capability: the
 * board's own query keys (the licensing page's tab, the board's view, its
 * status pill and its search), written once here so the console's "Open in
 * Backoffice", the Backoffice feature panels and the board agree on them.
 * `status=all` because a capability the developer looks up may already be
 * decided. The board has two views: the capabilities (console surfaces,
 * from the change feed) and the content (map layers and base maps).
 *
 * The board was called "What's new" until October 2026, when it outgrew the
 * name (it lists every feature, not the recent ones). These identifiers and
 * the URL keys keep the old word on purpose: bookmarks, the console's
 * links and the layer sheets' links all carry `changes`, and a rename
 * would break every one of them for a label.
 */
const WHATS_NEW_TABS_ID = "licensing-tabs"

export const WHATS_NEW_LINK = {
  path: "/licensing",
  tabsId: WHATS_NEW_TABS_ID,
  // The Backoffice Tabs component keys its URL state as `${id}-tab`.
  tabParam: `${WHATS_NEW_TABS_ID}-tab`,
  tabValue: "changes",
  viewParam: "changes-view",
  viewCapabilities: "capabilities",
  viewContent: "content",
  statusParam: "changes-status",
  statusAll: "all",
  searchParam: "changes-search",
} as const

export type WhatsNewView =
  | typeof WHATS_NEW_LINK.viewCapabilities
  | typeof WHATS_NEW_LINK.viewContent

/**
 * The query string that opens Feature access on one feature's row: the tab, the
 * view it is decided on, every status, and the code in the search box.
 */
export function whatsNewQuery(
  featureCode: string,
  view: WhatsNewView = WHATS_NEW_LINK.viewCapabilities,
) {
  const params = new URLSearchParams({
    [WHATS_NEW_LINK.tabParam]: WHATS_NEW_LINK.tabValue,
    [WHATS_NEW_LINK.viewParam]: view,
    [WHATS_NEW_LINK.statusParam]: WHATS_NEW_LINK.statusAll,
    [WHATS_NEW_LINK.searchParam]: featureCode,
  })
  return params.toString()
}

export function whatsNewUrl(backofficeOrigin: string, featureCode: string) {
  return `${backofficeOrigin}${WHATS_NEW_LINK.path}?${whatsNewQuery(featureCode)}`
}
